import React, { useState } from "react";
import { toast } from "react-toastify";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import { couponService } from "../services";
import { ApplicableOn, DiscountType } from "../utils/types";

type AddCouponProps = {
  setIsAddCouponOpen: (open: boolean) => void;
  onCreated: () => void;
};

const toDateInput = (date: Date) => date.toISOString().slice(0, 10);

const AddCoupon = ({ setIsAddCouponOpen, onCreated }: AddCouponProps) => {
  const [couponCode, setCouponCode] = useState("");
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState<DiscountType>("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [maxDiscountCap, setMaxDiscountCap] = useState("");
  const [minOrderValue, setMinOrderValue] = useState("");
  const [applicableOn, setApplicableOn] = useState<ApplicableOn>("all");
  const [startDate, setStartDate] = useState(toDateInput(new Date()));
  const [endDate, setEndDate] = useState(
    toDateInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))
  );
  const [usageLimitGlobal, setUsageLimitGlobal] = useState("");
  const [usageLimitPerUser, setUsageLimitPerUser] = useState("1");
  const [loading, setLoading] = useState(false);

  const inputClass =
    "w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00]";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponCode.trim()) return toast.error("Please enter a coupon code");
    if (!description.trim()) return toast.error("Please enter a description");
    if (!discountValue || Number(discountValue) <= 0) {
      return toast.error("Please enter a valid discount value");
    }
    if (discountType === "percentage" && Number(discountValue) > 100) {
      return toast.error("Percentage discount cannot exceed 100%");
    }
    if (!minOrderValue || Number(minOrderValue) < 0) {
      return toast.error("Please enter a valid minimum order value");
    }
    if (!usageLimitGlobal || Number(usageLimitGlobal) <= 0) {
      return toast.error("Please enter a valid global usage limit");
    }
    if (!usageLimitPerUser || Number(usageLimitPerUser) <= 0) {
      return toast.error("Please enter a valid per-user limit");
    }
    if (new Date(startDate) >= new Date(endDate)) {
      return toast.error("End date must be after start date");
    }

    setLoading(true);
    try {
      const response: any = await couponService.createCoupon({
        couponCode: couponCode.toUpperCase().trim(),
        description: description.trim(),
        discountType,
        discountValue: Number(discountValue),
        maxDiscountCap: maxDiscountCap ? Number(maxDiscountCap) : undefined,
        minOrderValue: Number(minOrderValue),
        applicableOn,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(`${endDate}T23:59:59`).toISOString(),
        usageLimitGlobal: Number(usageLimitGlobal),
        usageLimitPerUser: Number(usageLimitPerUser),
      });
      if (response.status) {
        toast.success("Coupon created successfully");
        onCreated();
        setIsAddCouponOpen(false);
      } else {
        toast.error(response.message || "Failed to create coupon");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to create coupon");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[70]"
      onClick={() => setIsAddCouponOpen(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 rounded-l-[20px] overflow-y-auto flex flex-col"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 z-10 px-6 py-4 border-b dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={arrowLeft}
                alt="back"
                className="w-[36px] h-[36px] rounded-full border cursor-pointer p-2"
                onClick={() => setIsAddCouponOpen(false)}
              />
              <h2 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[20px] font-semibold">
                Create Coupon
              </h2>
            </div>
            <img
              src={cancel}
              alt="close"
              className="w-[36px] h-[36px] p-2 rounded-full border cursor-pointer"
              onClick={() => setIsAddCouponOpen(false)}
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 px-6 py-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Coupon code</label>
            <input className={inputClass} value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="SAVE20" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="20% off on all orders" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Discount type</label>
              <select className={inputClass} value={discountType} onChange={(e) => setDiscountType(e.target.value as DiscountType)}>
                <option value="percentage">Percentage</option>
                <option value="flat">Flat amount</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Discount value</label>
              <input className={inputClass} type="number" min="1" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} />
            </div>
          </div>
          {discountType === "percentage" && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Max discount cap (optional)</label>
              <input className={inputClass} type="number" min="0" value={maxDiscountCap} onChange={(e) => setMaxDiscountCap(e.target.value)} />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Minimum order value</label>
            <input className={inputClass} type="number" min="0" value={minOrderValue} onChange={(e) => setMinOrderValue(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Applicable on</label>
            <select className={inputClass} value={applicableOn} onChange={(e) => setApplicableOn(e.target.value as ApplicableOn)}>
              <option value="all">All orders</option>
              <option value="specific">Specific items</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Start date</label>
              <input className={inputClass} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">End date</label>
              <input className={inputClass} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Global usage limit</label>
              <input className={inputClass} type="number" min="1" value={usageLimitGlobal} onChange={(e) => setUsageLimitGlobal(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Per user limit</label>
              <input className={inputClass} type="number" min="1" value={usageLimitPerUser} onChange={(e) => setUsageLimitPerUser(e.target.value)} />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 text-white font-Urbanist text-[16px] font-semibold disabled:opacity-50"
            style={{ borderRadius: 150, background: "#F30" }}
          >
            {loading ? "Creating..." : "Create Coupon"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddCoupon;
